/**
 * Clients for the new console (Traci's review, 23 Sep 2026; prototype v108 §4). The index searches every client
 * account; a client record holds contact information (Admin and Sales may edit), the number of bookings, every
 * booking (each opens on the Bookings tab, active or archived) and general client notes. Booking information lives on
 * the booking.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, MessageSquare, Pencil, Search } from 'lucide-react';
import AdminChatPanel from '@/components/admin/AdminChatPanel';
import toast from 'react-hot-toast';
import { Input } from '@/components/admin/ui/input';
import { Button } from '@/components/admin/ui/button';
import { Textarea } from '@/components/admin/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { initials } from '@/lib/staging/initials';
import { STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch, shortDate } from '../api';
import { useStaging } from '../StagingLayout';
import { StatusPill } from './BookingsPage';
import NewFlagDialog from '../components/NewFlagDialog';
import NoteList from '../components/NoteList';

export function ClientsIndex() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const page = Math.max(0, Number(params.get('page')) || 0);
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      stagingFetch(`/api/admin/staging/clients?q=${encodeURIComponent(q)}&page=${page}`).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message || String(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, page]);

  const go = (p: number) => { const next = new URLSearchParams(params); if (p) next.set('page', String(p)); else next.delete('page'); setParams(next, { replace: true }); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-8 space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Clients</h2>
        <p className="text-sm text-muted-foreground mt-1">Search every client account, then open one. Booking-specific information lives on each booking.</p>
      </div>
      <label className="relative block max-w-xl">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" value={q} aria-label="Search clients" placeholder="Search by name, email or phone…" onChange={(e) => { setQ(e.target.value); const next = new URLSearchParams(params); if (e.target.value) next.set('q', e.target.value); else next.delete('q'); next.delete('page'); setParams(next, { replace: true }); }} />
      </label>
      {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
      {!data && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {data && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-2 text-sm"><b>Client Database</b><span className="text-muted-foreground tabular-nums">{data.total.toLocaleString('en-US')} clients</span></div>
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-4 py-2">Client</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Phone</th><th className="px-4 py-2">Bookings</th><th className="px-4 py-2">Active</th></tr></thead>
            <tbody className="divide-y">
              {data.rows.map((c: any) => (
                <tr key={c.id} className="hover:bg-accent/40">
                  <td className="px-4 py-2"><Link className="flex items-center gap-2 hover:underline" to={`${STAGING_ROOT}/clients/${c.id}`}><span aria-hidden="true" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-muted text-[10px] font-semibold">{initials(c.full_name || c.email)}</span><span><span className="block font-medium">{c.full_name || 'Unnamed client'}</span><span className="block font-mono text-[10px] text-muted-foreground">{String(c.id).slice(0, 8).toUpperCase()}</span></span></Link></td>
                  <td className="px-4 py-2 text-muted-foreground">{c.email || '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.phone || '—'}</td>
                  <td className="px-4 py-2 tabular-nums">{c.bookings}</td>
                  <td className="px-4 py-2 tabular-nums">{c.active}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No clients match.</td></tr>}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t px-4 py-2 text-sm">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => go(page - 1)}>Previous</Button>
            <span className="text-muted-foreground">Page {page + 1} of {pages}</span>
            <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => go(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ClientRecord() {
  const { clientId } = useParams();
  const { role } = useStaging();
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [edit, setEdit] = useState<null | { name: string; email: string; phone: string }>(null);
  const [saving, setSaving] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const load = () => stagingFetch(`/api/admin/staging/clients/${clientId}`).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message || String(e)));
  useEffect(() => { setData(null); load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className="p-8"><Link to={`${STAGING_ROOT}/clients`} className="text-sm hover:underline">← Back to Clients</Link><p className="mt-4 text-sm text-destructive">Could not load: {error}</p></div>;
  if (!data) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  // Contact edit: Admin and Sales (§5.1). View As Billing sees it read-only.
  const canEdit = data.can_edit_contact && (role === 'admin' || role === 'sales');
  const c = data.client;
  const save = async () => {
    if (!edit) return;
    setSaving(true);
    try { await stagingFetch(`/api/admin/staging/clients/${clientId}`, { method: 'PATCH', body: JSON.stringify(edit) }); toast.success('Contact updated'); setEdit(null); await load(); }
    catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await stagingFetch(`/api/admin/staging/clients/${clientId}/notes`, { method: 'POST', body: JSON.stringify({ note }) }); setNote(''); await load(); }
    catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };

  return (
    <div className="p-8 space-y-5">
      <Link to={`${STAGING_ROOT}/clients`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Clients</Link>
      <section className="rounded-xl border bg-card p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div><h2 className="text-2xl font-semibold">{c.name || 'Unnamed client'}</h2><p className="text-xs text-muted-foreground">Client ID <span className="font-mono">{String(c.id).slice(0, 8).toUpperCase()}</span> · client since {shortDate(c.created_at)}</p></div>
          <div className="flex flex-wrap gap-2">
            <NewFlagDialog clientId={c.id} clientName={c.name} />
            {/* Chat with Client (prototype v108 §4): the roles that handle client conversations (Messages by role) */}
            {['admin', 'sales', 'billing'].includes(role) && <Button size="sm" onClick={() => setChatOpen(true)}><MessageSquare className="h-3.5 w-3.5 mr-1" /> Chat with Client</Button>}
            {canEdit && <Button size="sm" variant="outline" onClick={() => setEdit({ name: c.name || '', email: c.email || '', phone: c.phone || '' })}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit contact</Button>}
          </div>
        </div>
        <dl className="mt-4 grid gap-3 sm:grid-cols-3">
          <div><dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Email</dt><dd className="text-sm">{c.email || '—'}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Phone</dt><dd className="text-sm">{c.phone || '—'}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Bookings</dt><dd className="text-sm tabular-nums">{data.bookings.length}</dd></div>
        </dl>
      </section>

      <section className="rounded-xl border bg-card p-5 shadow-sm">
        <h3 className="font-semibold">Bookings</h3>
        <p className="text-xs text-muted-foreground">Every booking for this client, active or archived. Open one for the Full Booking.</p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {data.bookings.length === 0 && <p className="py-3 text-sm text-muted-foreground">No bookings for this client.</p>}
          {data.bookings.map((b: any) => (
            <Link key={b.id} to={`${STAGING_ROOT}/bookings/${b.id}`} aria-label={`Booking ${shortDate(b.event_date)}`} className="block rounded-lg border bg-background p-3 text-sm hover:bg-accent/40">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{shortDate(b.event_date)}{b.location ? ` · ${b.location}` : ''}</div>
                  <div className="text-xs text-muted-foreground">{b.package} · <span className="font-mono">{String(b.id).slice(0, 8).toUpperCase()}</span></div>
                </div>
                <StatusPill status={b.status} />
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div><dt className="text-[10px] uppercase text-muted-foreground">Package</dt><dd className="text-xs">{b.package}</dd></div>
                <div><dt className="text-[10px] uppercase text-muted-foreground">Assigned Talent</dt><dd className="text-xs">{b.talent?.length ? b.talent.map((t: any) => t.name || t.role_label).join(', ') : 'Not assigned'}</dd></div>
                <div><dt className="text-[10px] uppercase text-muted-foreground">Booking Date</dt><dd className="text-xs">{shortDate(b.booking_date)}</dd></div>
                <div><dt className="text-[10px] uppercase text-muted-foreground">Status</dt><dd className="text-xs">{b.status}</dd></div>
              </dl>
            </Link>
          ))}
        </div>
      </section>

      <section className="rounded-xl border bg-card p-5 shadow-sm">
        <h3 className="font-semibold">General Client Notes</h3>
        <p className="text-xs text-muted-foreground">Client-level notes only. Booking-specific notes belong on the booking.</p>
        <div className="mt-3 max-h-[300px] overflow-y-auto space-y-2">
          <NoteList notes={data.notes} basePath={`/api/admin/staging/clients/${clientId}/notes`} empty="No client-level notes yet." onChanged={load} />
        </div>
        <div className="mt-3 flex gap-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a general note about this client…" rows={2} />
          <Button onClick={addNote} disabled={saving || !note.trim()}>Add Note</Button>
        </div>
      </section>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit contact</DialogTitle><DialogDescription>The email is also the client&apos;s sign-in, so it changes on their login too. Every change is written to the client notes.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <label className="block text-sm">Name<Input className="mt-1" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
              <label className="block text-sm">Email<Input className="mt-1" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></label>
              <label className="block text-sm">Phone<Input className="mt-1" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></label>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={saving || !edit.name.trim()}>Save</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {chatOpen && <AdminChatPanel userId={c.id} userName={c.name || 'Client'} userRole="customer" onClose={() => setChatOpen(false)} />}
    </div>
  );
}
